import React, { useEffect, useRef, useState } from 'react';
import { Box, Dialog, HStack, Portal, useBreakpointValue } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { CloseButton } from '@/components/ui/close-button.tsx';
import PdfDocumentRenderer from './shared/PdfDocumentRenderer';
import PdfFloatingControls from './shared/PdfFloatingControls';
import ImageDocumentRenderer from './shared/ImageDocumentRenderer';
import { usePdfScrollSync } from '@/hooks/viewers/usePdfScrollSync.ts';
import { PageNumberReasonChange, PdfFileSource } from '@/hooks/viewers/usePdfViewer.ts';
import PdfPageSidebar, { PDF_PAGE_SIDEBAR_DEFAULT_WIDTH } from './shared/PdfPageSidebar.tsx';
import { LuPanelLeftClose, LuPanelLeftOpen } from 'react-icons/lu';

interface ViewerBaseProps {
	title: string;
	isOpen: boolean;
	onClose: () => void;
}

interface ViewerPdfProps extends ViewerBaseProps {
	fileKind?: 'pdf';
	pageNumber: number;
	numPages: number;
	loading: boolean;
	pdfOptions: PdfFileSource;
	onDocumentLoadSuccess: ({ numPages }: { numPages: number }) => void;
	onDocumentLoadError: (error: Error) => void;
	goToPrevPage: () => void;
	goToNextPage: () => void;
	onPageChange: (page: number, reason: PageNumberReasonChange) => void;
	setPageChangeReason: (callback: (reason: PageNumberReasonChange) => void) => void;
	showAllPages?: boolean;
}

interface ViewerImageProps extends ViewerBaseProps {
	fileKind: 'image';
	src: string;
	loading?: boolean;
}

type ViewerFullScreenDialogProps = ViewerPdfProps | ViewerImageProps;

// Horizontal padding (px per side) the mobile PDF container reserves. Used both as the container's
// `base` padding and subtracted from the measured width for fit-to-width, so the two can't drift.
const MOBILE_PDF_PADDING_X = 12;

/**
 * Full screen PDF dialog component for expanded PDF viewing
 * Displays PDF in a modal dialog with navigation controls
 */
const ViewerFullScreenDialog: React.FC<ViewerFullScreenDialogProps> = (props) => {
	const { title, isOpen, onClose } = props;
	const openedAtRef = useRef<number>(0);

	// Use natural discriminant: presence of `src` narrows to image props
	const isImageKind = 'src' in props;
	const showAllPdfPages = !isImageKind && ((props as ViewerPdfProps).showAllPages ?? true);

	// Zoom state (explicitly control initial zoom)
	const [scale, setScale] = useState(1);
	const [rotation, setRotation] = useState(0);
	const [isPageSidebarOpen, setIsPageSidebarOpen] = useState(true);
	const [pageSidebarWidth, setPageSidebarWidth] = useState(PDF_PAGE_SIDEBAR_DEFAULT_WIDTH);

	const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
	// Gates the mount, not just the paint. The sidebar renders one react-pdf <Page> per page, and a
	// `display: none` ancestor does not stop React mounting it — every page of the document would be
	// rasterised a second time, invisibly, on phones and tablets.
	const canShowPageSidebar = useBreakpointValue({ base: false, lg: true }) ?? false;
	const showPageSidebarUi = showAllPdfPages && canShowPageSidebar;

	// Mobile renders PDF pages at the viewer's width (fit-to-width) rather than native size, which
	// would overflow a phone screen. We measure the scrollable body and render each page at that
	// width × the zoom scale, so the zoom controls still apply. Desktop keeps native scale rendering.
	const [bodyWidth, setBodyWidth] = useState(0);

	// Store cleanup function reference
	const cleanupScrollEventsRef = useRef<(() => void) | null>(null);

	// Provide safe fallbacks for image mode to satisfy hook contract without side effects
	const setPageChangeReasonSafe = isImageKind
		? (((/* cb */) => {
				/* no-op in image mode */
			}) as (cb: (reason: PageNumberReasonChange) => void) => void)
		: (props as ViewerPdfProps).setPageChangeReason;
	const onPageChangeSafe = isImageKind
		? (((/* page, reason */) => {
				/* no-op in image mode */
			}) as (page: number, reason: PageNumberReasonChange) => void)
		: (props as ViewerPdfProps).onPageChange;

	// Values for the scroll sync hook (must always be provided)
	const hookCurrentPage = isImageKind ? 1 : (props as ViewerPdfProps).pageNumber;
	const hookTotalPages = isImageKind ? 1 : (props as ViewerPdfProps).numPages;
	const hookLoading = isImageKind
		? ((props as ViewerImageProps).loading ?? false)
		: (props as ViewerPdfProps).loading;

	// Custom hook for managing PDF scroll synchronization (always called to keep hooks order)
	const { scrollContainerRef, scrollToPage, registerScrollEvents, registerPageRef } = usePdfScrollSync({
		isOpen: isOpen && showAllPdfPages,
		currentPageNumber: hookCurrentPage,
		totalPages: hookTotalPages,
		onPageNumberChange: onPageChangeSafe,
		setPageChangeReason: setPageChangeReasonSafe,
		loading: hookLoading,
	});

	const onLoadSuccess = (args: { numPages: number }) => {
		if (!isImageKind) {
			(props as ViewerPdfProps).onDocumentLoadSuccess(args);

			// Clean up any existing listeners first
			if (cleanupScrollEventsRef.current) {
				cleanupScrollEventsRef.current();
			}

			// Register new scroll events and store cleanup function
			cleanupScrollEventsRef.current = registerScrollEvents() || null;
		}
	};

	const onPageLoadSuccess = (_page: unknown, loadedPageNumber: number) => {
		if (!showAllPdfPages || loadedPageNumber !== hookCurrentPage) return;
		requestAnimationFrame(() => scrollToPage(hookCurrentPage));
	};

	// Clean up scroll events when dialog closes or component unmounts
	useEffect(() => {
		if ((!isOpen || !showAllPdfPages) && cleanupScrollEventsRef.current) {
			cleanupScrollEventsRef.current();
			cleanupScrollEventsRef.current = null;
		}
	}, [isOpen, showAllPdfPages]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (cleanupScrollEventsRef.current) {
				cleanupScrollEventsRef.current();
			}
		};
	}, []);

	useEffect(() => {
		if (isOpen) openedAtRef.current = Date.now();
	}, [isOpen]);

	// Track the body width so mobile can fit PDF pages to it.
	useEffect(() => {
		const el = scrollContainerRef.current;
		if (!isOpen || !showAllPdfPages || !el) return;
		const update = () => setBodyWidth(el.clientWidth);
		update();
		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => observer.disconnect();
	}, [isOpen, showAllPdfPages, scrollContainerRef]);

	const handleZoomIn = () => setScale((s) => Math.min(3, +(s + 0.1).toFixed(2)));
	const handleZoomOut = () => setScale((s) => Math.max(0.5, +(s - 0.1).toFixed(2)));
	const handleZoomReset = () => setScale(1);
	const handleRotateClockwise = () => setRotation((current) => (current + 90) % 360);
	const hidePageSidebar = () => setIsPageSidebarOpen(false);
	const showPageSidebar = () => setIsPageSidebarOpen(true);

	// Fit-to-width sizing for mobile: page width = available body width (minus padding) × zoom.
	// Falls back to scale rendering until the body has been measured (pdfFitWidth === 0).
	const pdfFitWidth = Math.max(0, bodyWidth - MOBILE_PDF_PADDING_X * 2);
	const useFitWidth = isMobile && pdfFitWidth > 0;
	const pdfRenderWidth = Math.round(pdfFitWidth * scale);
	const controlsLeft = showPageSidebarUi && isPageSidebarOpen ? `calc(50% + (${pageSidebarWidth}px / 2))` : '50%';
	const requestClose = () => {
		// Ignore dismiss events caused by the pointer/click that opened the fullscreen viewer.
		if (Date.now() - openedAtRef.current < 120) return;
		onClose();
	};

	return (
		<Dialog.Root
			scrollBehavior="inside"
			size="full"
			placement="center"
			open={isOpen}
			onOpenChange={({ open }) => {
				if (open) return;
				requestClose();
			}}
		>
			<Portal>
				<Dialog.Backdrop />
				<Dialog.Positioner>
					<Dialog.Content
						maxW="100vw"
						h="100dvh"
						display="flex"
						flexDirection="column"
						m={0}
						borderRadius={0}
						bg="gray.600/70"
						backdropFilter="blur(8px)"
					>
						<Dialog.Header
							bg="gray.900"
							backdropFilter="blur(8px)"
							px={6}
							py={4}
							position="sticky"
							top={0}
							zIndex={1}
							alignItems="center"
							// borderBottom="1px solid"
							// borderBottomColor="gray.700"
						>
							<HStack gap={2} minW={0} flex="1">
								{showPageSidebarUi && (
									<IconButton
										aria-label={isPageSidebarOpen ? 'Hide page sidebar' : 'Show page sidebar'}
										size="xs"
										variant="ghost"
										color="whiteAlpha.800"
										flexShrink={0}
										_hover={{ bg: 'whiteAlpha.200', color: 'white' }}
										onClick={isPageSidebarOpen ? hidePageSidebar : showPageSidebar}
									>
										{isPageSidebarOpen ? <LuPanelLeftClose /> : <LuPanelLeftOpen />}
									</IconButton>
								)}
								<Dialog.Title color="white" truncate minW={0}>
									{title ?? (isImageKind ? 'Image Viewer' : 'PDF Viewer')}
								</Dialog.Title>
							</HStack>
							{/* Chakra's closeTrigger slot is `position: absolute`, which takes the button out of
							    flow and lets a long title run underneath it. Static returns it to the header's
							    flex row, so the title truncates against it instead. */}
							<Dialog.CloseTrigger asChild>
								<CloseButton
									size="lg"
									color="white"
									_hover={{ bg: 'gray.700' }}
									position="static"
									flexShrink={0}
									mr={{ base: 0, md: 10 }}
								/>
							</Dialog.CloseTrigger>
						</Dialog.Header>

						<Dialog.Body
							p={0}
							flex="1"
							minH={0}
							bg="transparent"
							position="relative"
							onClick={(event) => {
								if (event.target === event.currentTarget) requestClose();
							}}
						>
							{!isImageKind ? (
								<Box display="flex" w="full" h="full" minH={0}>
									{showPageSidebarUi && (
										<PdfPageSidebar
											isOpen={isPageSidebarOpen}
											width={pageSidebarWidth}
											pdfOptions={(props as ViewerPdfProps).pdfOptions}
											numPages={(props as ViewerPdfProps).numPages}
											pageNumber={(props as ViewerPdfProps).pageNumber}
											loading={(props as ViewerPdfProps).loading}
											rotate={rotation}
											onPageChange={(props as ViewerPdfProps).onPageChange}
											onWidthChange={setPageSidebarWidth}
										/>
									)}
									<Box
										ref={scrollContainerRef}
										flex="1"
										minW={0}
										minH={0}
										overflow="auto"
										onClick={(event) => {
											if (event.target === event.currentTarget) requestClose();
										}}
									>
										<PdfDocumentRenderer
											pdfOptions={(props as ViewerPdfProps).pdfOptions}
											pageNumber={(props as ViewerPdfProps).pageNumber}
											scale={useFitWidth ? undefined : scale}
											rotate={rotation}
											width={useFitWidth ? pdfRenderWidth : undefined}
											loading={(props as ViewerPdfProps).loading}
											onLoadSuccess={onLoadSuccess}
											onLoadError={(props as ViewerPdfProps).onDocumentLoadError}
											showAllPages={showAllPdfPages}
											numPages={(props as ViewerPdfProps).numPages}
											onBackgroundClick={requestClose}
											onPageLoadSuccess={onPageLoadSuccess}
											onPageElementRef={registerPageRef}
											containerProps={{
												w: 'full',
												minH: 'full',
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												justifyContent: 'flex-start',
												pt: 0,
												p: { base: `${MOBILE_PDF_PADDING_X}px`, md: 6 },
											}}
											spinnerColor="primary"
											spinnerSize="xl"
										/>
									</Box>
								</Box>
							) : (
								<ImageDocumentRenderer
									src={(props as ViewerImageProps).src}
									loading={(props as ViewerImageProps).loading ?? false}
									onBackgroundClick={requestClose}
									containerProps={{
										w: 'full',
										h: 'full',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										p: { base: 3, md: 6 },
									}}
									scale={scale}
									spinnerSize="xl"
								/>
							)}
						</Dialog.Body>
						<Dialog.Footer position="absolute" bottom={0} left={0} right={0} p={0} pointerEvents="none">
							{!isImageKind && (
								<Box pointerEvents="auto">
									<PdfFloatingControls
										pageNumber={(props as ViewerPdfProps).pageNumber}
										numPages={(props as ViewerPdfProps).numPages}
										loading={(props as ViewerPdfProps).loading}
										onPrevPage={(props as ViewerPdfProps).goToPrevPage}
										onNextPage={(props as ViewerPdfProps).goToNextPage}
										onPageChange={(props as ViewerPdfProps).onPageChange}
										scale={scale}
										onZoomIn={handleZoomIn}
										onZoomOut={handleZoomOut}
										onZoomReset={handleZoomReset}
										onRotateClockwise={handleRotateClockwise}
										left={controlsLeft}
									/>
								</Box>
							)}
						</Dialog.Footer>
					</Dialog.Content>
				</Dialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
};

export default ViewerFullScreenDialog;
